import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import store from '@/store'
import vuetify from '@/plugins/vuetify'
import WidgetTemplate from '@/views/Project/Dashboard/components/widget.vue'
import LineChart from '@/components/Charts/Line/Line.vue'
import BarChart from '@/components/Charts/Bar/Bar.vue'
import PieChart from '@/components/Charts/Pie.vue'
import { Debounce, Bind } from 'lodash-decorators'
import { numberFormat } from '@/types/observation'
import CalendarPicker from '@/components/Picker/Calendar/Calendar.vue'
import type MixinInterface from '@/_mixins'
import CompanyMixin from '@/_mixins/company'
import { i18n } from '@/plugins/i18n'
import { getRequest } from '@/plugins/api-axios'
import { deattribute, deserialise } from 'kitsu-core'
import { getURL } from '@/utils/axios'

@Component({
    mixins: [CompanyMixin],
    name: 'PersonnelStats',
    components: { CalendarPicker, WidgetTemplate, LineChart, BarChart, PieChart },
})
export default class PersonnelStats extends Mixins<MixinInterface>() {
    @Prop({ required: true }) component!: any
    @Prop({ required: true }) dashboardId!: string
    @Prop({ required: true }) project!: any

    store = store
    vuetify: any = vuetify
    datasets: any = []
    labels: any = {}
    loading = true
    i18n = i18n

    get widget() {
        return this.store.getters.widgetData(this.component.id)
    }

    get charts() {
        return [
            { name: 'bar', value: 'bar' },
            { name: 'line', value: 'line' },
            { name: 'pie', value: 'pie', disabled: !!this.settings.aggregatePeriod },
        ]
    }

    get viewportWidth() {
        return this.vuetify.framework.breakpoint?.width
    }

    get settingProject() {
        return this.settings.project ? this.store.getters.project(this.settings.project) : null
    }

    get permissions() {
        let permissionList: Array<string> = []
        if (this.settingProject) {
            permissionList = this.settingProject.attributes.permissions
        } else {
            Object.values(this.store.getters.projects).forEach((project: any) => {
                permissionList = [...permissionList, ...project.attributes.permissions]
            })
        }
        return permissionList
    }

    get hasAccess() {
        return (
            this.permissions.includes('project.view_integrations') ||
            this.permissions.includes('personnel.read')
        )
    }

    get aggregatePeriods() {
        return ['day', 'week', 'month', 'quarter', 'year']
    }

    get skillLevels() {
        return ['apprentice', 'skilledWorker']
    }

    get cardTypes() {
        return ['standard', 'visitor', 'virtual']
    }

    get aggregateField() {
        return ['peopleOnSite', 'newPeopleOnSite']
    }

    get groupBy() {
        return ['skillLevel', 'cardType', 'cardCompany']
    }

    get groupByItemMetadata() {
        return {
            skillLevel: {
                apprentice: {
                    color: 'info',
                },
                skilledWorker: {
                    color: 'primary',
                },
            },
            cardType: {
                standard: {
                    color: 'primary',
                },
                visitor: {
                    color: 'accent',
                },
                virtual: {
                    color: 'success',
                },
            },
        }
    }

    get groupByDataFormat() {
        return numberFormat
    }

    settingsBase: any = {}

    get placement() {
        let result: any = {}
        Object.keys(this.widget.placement.default).forEach((key) => {
            result[key] =
                this.widget.placement[this.device(this.viewportWidth)][key] ||
                typeof this.widget.placement[this.device(this.viewportWidth)][key] == 'boolean'
                    ? this.widget.placement[this.device(this.viewportWidth)][key]
                    : this.widget.placement.default[key]
        })
        return result
    }

    get settings() {
        let result: any = {}
        if (this.widget) {
            Object.keys(this.widget.settings.default).forEach((key) => {
                result[key] =
                    this.widget.settings[this.device(this.viewportWidth)][key] ||
                    typeof this.widget.settings[this.device(this.viewportWidth)][key] == 'boolean'
                        ? this.widget.settings[this.device(this.viewportWidth)][key]
                        : this.widget.settings.default[key]
            })
        }

        return result
    }

    get isSettingsUpdated() {
        return !this.isObjectEmpty(this.difference(this.settings, this.settingsBase))
    }

    happenedAtMenu = false

    get max() {
        return new Date().toISOString().substr(0, 10)
    }

    get happenedAtValues() {
        let text: any = ''
        if (this.settings?.happenedAt?.period) {
            text = this.getTranslation(
                `calendar.period_last_${this.settings?.happenedAt?.period}_days`
            )
        } else if (this.settings?.happenedAt?.ge || this.settings?.happenedAt?.le) {
            if (!this.settings.happenedAt.le) {
                text = `from ${this.generalFormat(
                    this.settings.happenedAt.ge,
                    this.timeZone,
                    'DD.MM.YYYY'
                )}`
            } else if (!this.settings.happenedAt.ge) {
                text = `before ${this.generalFormat(
                    this.settings.happenedAt.le,
                    this.timeZone,
                    'DD.MM.YYYY'
                )}`
            } else {
                text = `${this.generalFormat(
                    this.settings.happenedAt.ge,
                    this.timeZone,
                    'DD.MM.YYYY'
                )} - ${this.generalFormat(
                    this.settings.happenedAt.le,
                    this.timeZone,
                    'DD.MM.YYYY'
                )}`
            }
        }
        return text
    }

    get happenedAtStart() {
        return this.settings?.happenedAt?.ge
    }

    get happenedAtEnd() {
        return this.settings?.happenedAt?.le
    }

    get happenedAtPeriod() {
        return this.settings?.happenedAt?.period
    }

    passHappenedAt(data: any) {
        this.update('happenedAt', data)
    }

    // FILTER BY COMPANY
    companies: any = []

    update(key: string, value: any) {
        let widgets = JSON.parse(JSON.stringify(this.store.getters.widgetsData))
        let widget = widgets[this.component.id]

        if (!widget.settings.default.hasOwnProperty(key)) {
            widget.settings.default[key] = null
        }
        widget.settings[this.viewType][key] = value
        this.store.commit('widgetsData', widgets)
    }

    @Watch('settings', { immediate: true })
    @Debounce(1000)
    @Bind()
    async getStat() {
        if (
            this.isSettingsUpdated &&
            (this.isObjectEmpty(this.settingsBase) ||
                this.settingsBase?.chartType == this.settings.chartType)
        ) {
            this.loading = true
            let params = {
                include: 'groupResource',
                ...(this.settings.group &&
                    !this.settings.project && { 'filter[projectGroup]': this.settings.group }),
                ...(this.settings.project && { 'filter[project]': this.settings.project }),
                ...(this.settings.skillLevel && { 'filter[skillLevel]': this.settings.skillLevel }),
                ...(this.settings.cardType && { 'filter[cardType]': this.settings.cardType }),
                ...((this.settings?.happenedAt?.period || this.settings?.happenedAt?.ge) && {
                    'filter[time][ge]': this.settings.happenedAt.period
                        ? this.generalFormat(
                              new Date(Date.now() - 86400 * 1000 * this.settings.happenedAt.period),
                              this.timeZone
                          )
                        : this.settings.happenedAt.ge,
                }),
                ...((this.settings?.happenedAt?.period || this.settings?.happenedAt?.le) && {
                    'filter[time][lt]': this.settings.happenedAt.period
                        ? this.generalFormat(new Date(Date.now()), this.timeZone)
                        : this.settings.happenedAt.le,
                }),
                ...(this.settings.aggregateField && {
                    aggregateField: this.settings.aggregateField,
                }),
                ...(this.settings.aggregatePeriod && {
                    aggregatePeriod: this.settings.aggregatePeriod,
                }),
                ...(this.settings.groupBy && {
                    groupBy: this.settings.groupBy,
                }),
                ...(this.settings.cardCompanies &&
                    this.settings.cardCompanies.length > 0 && {
                        'filter[cardCompany]': this.settings.cardCompanies.join(),
                    }),
            }
            let personnelRes = await getRequest(
                getURL({
                    path: `personnelStatistics`,
                    base: process.env.VUE_APP_PROJECT_BASE_URL,
                    params,
                }),
                {}
            )

            let res = deserialise(personnelRes.data).data

            this.datasets = []
            this.labels = {}

            res.forEach((el: any) => {
                el.data.forEach((el: any) => {
                    el.p && (this.labels[el.p] = el.p)
                })
            })

            this.labels = Object.values(this.labels).sort((a: any, b: any) => {
                let dateA = new Date(a).getTime()
                let dateB = new Date(b).getTime()
                return dateA > dateB ? 1 : -1
            })

            if (this.labels.length === 0) {
                this.labels = ['']
            }

            res.forEach((item: any, index: number) => {
                let dataset: any = {
                    label: item.groupValue ? item.groupValue : '',
                    data: [],
                    borderColor: 'grey',
                    backgroundColor: 'grey',
                }

                let itemMetadata = this.groupByItemMetadata[this.settings.groupBy]
                let lightTheme = this.vuetify.framework.theme.themes.light
                switch (this.settings.groupBy) {
                    case 'skillLevel':
                        dataset.label = this.getTranslation(
                            `enum_personnelSkillLevel.${item.groupValue}`
                        )
                        dataset.borderColor = item.groupValue
                            ? lightTheme[itemMetadata[item.groupValue]?.color]
                            : 'grey'
                        dataset.backgroundColor = item.groupValue
                            ? lightTheme[itemMetadata[item.groupValue]?.color]
                            : 'grey'
                        break
                    case 'cardType':
                        dataset.label = this.getTranslation(
                            `enum_personnelCardType.${item.groupValue}`
                        )
                        dataset.borderColor = item.groupValue
                            ? lightTheme[itemMetadata[item.groupValue]?.color]
                            : 'grey'
                        dataset.backgroundColor = item.groupValue
                            ? lightTheme[itemMetadata[item.groupValue]?.color]
                            : 'grey'
                        break
                    default:
                        dataset.label = this.getLabel(item.groupResource)
                        let color =
                            index == 0
                                ? lightTheme.primary
                                : `#${Math.floor(Math.random() * 16777215).toString(16)}`
                        dataset.borderColor = color
                        dataset.backgroundColor = color
                        break
                }

                this.labels.forEach((date: any) => {
                    let data = item.data.find((item: any) => {
                        return this.settings.aggregatePeriod ? item.p == date : true
                    })

                    if (data) {
                        dataset.data.push({
                            x: this.generalFormat(date, this.timeZone, this.dateFormat),
                            y: data.v,
                        })
                    }
                })

                this.datasets.push(dataset)
            })

            this.dateFormat
                ? (this.labels = this.labels.map(
                      (date: any) =>
                          (date = this.generalFormat(date, this.timeZone, this.dateFormat))
                  ))
                : ''

            this.loading = false
        }
        this.settingsBase = this.settings
    }

    getLabel(groupResource: any) {
        let label: any = null

        if (groupResource.data?.id) {
            label = groupResource.data.name
        } else {
            label = this.settings.groupBy
                ? this.getTranslation(
                      `fe_personnel_statistics.groupBy_default_${this.settings.groupBy}`
                  )
                : this.getTranslation(`fe_personnel_statistics.label_unknown`)
        }

        return label
    }

    get timeZone() {
        return this.settingProject ? this.settingProject.attributes.timeZone : 'Europe/Oslo'
    }

    get dateFormat() {
        let data: any = null
        switch (this.settings.aggregatePeriod) {
            case 'day':
                data = 'DD.MM.YYYY'
                break
            case 'week':
                data = 'WW YYYY'
                break
            case 'month':
                data = "MMMM'YY"
                break
            case 'quarter':
                data = 'Q YYYY'
                break
            case 'year':
                data = 'YYYY'
                break
        }
        return data
    }

    async mounted() {
        if (!this.project?.id) return

        let hierarchyRes = await getRequest(
            getURL({
                path: `contractorHierarchies/${this.project.id}`,
                base: process.env.VUE_APP_CONTRACTOR_BASE_URL,
                params: {
                    include: 'nodes,nodes.company',
                    skipLinks: '',
                },
            }),
            {}
        )
        this.companies = deattribute(
            hierarchyRes.data.included.filter((el: any) => el.type === 'company')
        )
    }
}
